import { ProductVariant } from '@/entities/product';

import { DescriptionTab, ProductParsedDescription } from '../types';
import { DefaultDescriptionParseMapper } from './productDescription';

const replaceUnwantedNewLines = (description: string) => description.replace(/(\r\n|<br>)/g, '<br/>');

const parseKeyRegexp = (parseKey: string) => RegExp(`<(?:b|strong)>\\s*${parseKey}`, 'i');

type MappedString = {
    tabKey: DescriptionTab;
    mapperKey: string;
    startIndex: number;
    matchedString?: string;
};

const creatKeyIndex = (html: string, tabKey: DescriptionTab, mapperKey: string) => {
    const regexResult = parseKeyRegexp(mapperKey).exec(html);
    const resultIndex = regexResult?.index ?? -1;
    if (resultIndex !== -1) {
        return { tabKey, mapperKey, startIndex: resultIndex };
    }
};

const findStartIndexForMapperKeys = (html: string, parserMapper: ProductParsedDescription): MappedString[] =>
    Object.entries(parserMapper ?? {}).reduce<MappedString[]>((keyIndexes, [tabKey, mapperKeys]) => {
        const tab = tabKey as DescriptionTab;

        return keyIndexes.concat(
            mapperKeys.reduce<MappedString[]>((tabKeyIndexes, mapperKey) => {
                const keyIndex = creatKeyIndex(html, tab, mapperKey);
                if (keyIndex) {
                    tabKeyIndexes.push(keyIndex);
                }

                return tabKeyIndexes;
            }, [])
        );
    }, []);

const mapperKeyMatches = (htmlToParse: string, parserMapper: ProductParsedDescription): MappedString[] => {
    const matchedStrings = findStartIndexForMapperKeys(htmlToParse, parserMapper);

    if (!matchedStrings.length) {
        return [];
    }

    matchedStrings.sort((a, b) => a.startIndex - b.startIndex);

    const createString = (currentMatch: MappedString, index: number) => {
        currentMatch.matchedString = htmlToParse.substring(
            currentMatch.startIndex,
            matchedStrings.at(index + 1)?.startIndex // undefined is ok. Only mean rest of string
        );
    };

    matchedStrings.forEach(createString);

    return matchedStrings;
};

const stringsForTab = (tab: string, matchedKeys: MappedString[]) => {
    const tabKey = tab as DescriptionTab;
    return matchedKeys.reduce<Record<string, string>>((tabStrings, matchKey: MappedString) => {
        if (matchKey.tabKey === tabKey && !!matchKey.matchedString) {
            tabStrings[matchKey.mapperKey] = matchKey.matchedString;
        }

        return tabStrings;
    }, {});
};

const descriptionResultsAccordingToMapper = (
    parseMapper: ProductParsedDescription,
    matchedKeys: MappedString[]
): ProductParsedDescription =>
    Object.entries(parseMapper).reduce<ProductParsedDescription>((descriptionTabs, [tab, mapperKeys]) => {
        const mappedStringsForTab = stringsForTab(tab, matchedKeys);

        const tabStrings = mapperKeys.reduce<string[]>(
            (parsedStrings, mapperKey) =>
                parsedStrings.concat(mappedStringsForTab[mapperKey] ? [mappedStringsForTab[mapperKey]] : []),
            []
        );

        // Only add tab if it has non-empty content
        const hasContent = tabStrings.some(str => str && str.trim().length > 0);
        if (hasContent) {
            descriptionTabs[tab as keyof ProductParsedDescription] = tabStrings;
        }
        return descriptionTabs;
    }, {});

export const parseProductDescription = (
    longProductDescription: ProductVariant['description']
): ProductParsedDescription => {
    if (!longProductDescription) {
        return {};
    }

    const description = replaceUnwantedNewLines(longProductDescription);
    const matchedKeys = mapperKeyMatches(description, DefaultDescriptionParseMapper);

    // Fallback: if no keys matched, return entire description in Description tab
    if (description.trim().length > 0 && matchedKeys.length === 0) {
        return {
            Description: [description],
        };
    }

    return descriptionResultsAccordingToMapper(DefaultDescriptionParseMapper, matchedKeys);
};
